import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DoctorProfileService } from '../../../core/services/doctor-profile.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from '../../../core/services/notification.service';
import { strongPasswordValidator } from '../../../core/validators/password.validator';

@Component({
  selector: 'app-forgot-password-change',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './forgot-password-change.html',
  styleUrl: './forgot-password-change.scss',
})
export class ForgotPasswordChange implements OnInit {
  passwordHidden: boolean = true;
  confirmPasswordHidden: boolean = true;
  changePasswordForm!: FormGroup;
  isLoading: boolean = false;
  errorMessage: string | null = null;
  /** Pre-auth session token — must be OTP-verified before reaching this step */
  preAuthToken: string | undefined;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private profileService: DoctorProfileService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit() {
    this.preAuthToken = history.state['preAuthToken'];

    this.changePasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, strongPasswordValidator()]],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: (form) => {
          const pass = form.get('password')?.value;
          const confirm = form.get('confirmPassword')?.value;
          return pass === confirm ? null : { passwordMismatch: true };
        },
      },
    );
  }

  hidePassword() {
    return this.passwordHidden;
  }

  hideConfirmPassword() {
    return this.confirmPasswordHidden;
  }

  toggle(field: 'password' | 'confirmPassword') {
    if (field === 'password') this.passwordHidden = !this.passwordHidden;
    if (field === 'confirmPassword') this.confirmPasswordHidden = !this.confirmPasswordHidden;
  }

  hasUppercase(): boolean { return /[A-Z]/.test(this.changePasswordForm.get('password')?.value ?? ''); }
  hasLowercase(): boolean { return /[a-z]/.test(this.changePasswordForm.get('password')?.value ?? ''); }
  hasDigit(): boolean { return /\d/.test(this.changePasswordForm.get('password')?.value ?? ''); }
  hasSpecial(): boolean { return /[^A-Za-z0-9]/.test(this.changePasswordForm.get('password')?.value ?? ''); }

  submit() {
    this.errorMessage = null;

    // Validate that we have the pre-auth token
    if (!this.preAuthToken || this.preAuthToken.trim() === '') {
      this.notificationService.error('Session expired. Please request a new OTP.');
      this.errorMessage = 'Session expired. Please request a new OTP.';
      return;
    }

    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    this.profileService
      .changePasswordWithPreAuth(
        'FORGOT_PASSWORD',
        this.changePasswordForm.value.password,
        this.preAuthToken,
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.notificationService.success('Password updated successfully. You can now log in.');
          this.router.navigate(['login']);
        },
        error: (err) => {
          this.isLoading = false;
          const msg = err?.error?.message || 'Failed to update password. Please try again.';
          this.errorMessage = msg;
          this.notificationService.error(msg, 'Update Failed');
        },
      });
  }
}
